/**
 * TaskDialog - Dialog for creating/editing tasks
 */
import React, { useState, useEffect } from 'react';
import { Task, TaskType, TaskStatus, TASK_TYPE_LABELS, PRIORITY_LABELS, ChecklistItem } from '../../types/task';
import { useTaskStore } from '../../stores/taskStore';
import { useProjectStore } from '../../stores/projectStore';
import { usePoleStore } from '../../stores/poleStore';
import { useCableStore } from '../../stores/cableStore';

interface TaskDialogProps {
  task?: Task | null;
  isOpen: boolean;
  onClose: () => void;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({ task, isOpen, onClose }) => {
  const { currentProject } = useProjectStore();
  const { poles } = usePoleStore();
  const { cables } = useCableStore();
  const { createTask, updateTask } = useTaskStore();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: TaskType.OTHER,
    status: TaskStatus.TODO,
    pole_id: undefined as number | undefined,
    cable_id: undefined as number | undefined,
    onu_id: undefined as number | undefined,
    assigned_to: '',
    priority: 0,
    deadline: '',
    progress: 0,
    notes: '',
  });

  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        task_type: task.task_type,
        status: task.status,
        pole_id: task.pole_id,
        cable_id: task.cable_id,
        onu_id: task.onu_id,
        assigned_to: task.assigned_to || '',
        priority: task.priority,
        deadline: task.deadline ? task.deadline.split('T')[0] : '',
        progress: task.progress,
        notes: task.notes || '',
      });
      setChecklist(task.checklist || []);
    } else {
      // Reset form for new task
      setFormData({
        title: '',
        description: '',
        task_type: TaskType.OTHER,
        status: TaskStatus.TODO,
        pole_id: undefined,
        cable_id: undefined,
        onu_id: undefined,
        assigned_to: '',
        priority: 0,
        deadline: '',
        progress: 0,
        notes: '',
      });
      setChecklist([]);
    }
  }, [task, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentProject) return;

    const taskData: Partial<Task> = {
      ...formData,
      project_id: currentProject.id,
      checklist,
      attachments: task?.attachments || [],
    };

    if (task) {
      await updateTask(task.id, taskData);
    } else {
      await createTask(taskData);
    }

    onClose();
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const newItem: ChecklistItem = {
        id: Date.now(),
        text: newChecklistItem.trim(),
        completed: false,
      };
      setChecklist([...checklist, newItem]);
      setNewChecklistItem('');
    }
  };

  const handleToggleChecklistItem = (id: number) => {
    setChecklist(
      checklist.map((item) =>
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const handleDeleteChecklistItem = (id: number) => {
    setChecklist(checklist.filter((item) => item.id !== id));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {task ? 'Upraviť úlohu' : 'Nová úloha'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Názov úlohy *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Popis
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Task Type and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Typ úlohy *
              </label>
              <select
                value={formData.task_type}
                onChange={(e) => setFormData({ ...formData, task_type: e.target.value as TaskType })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priorita
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Pole, Cable, ONU */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stĺpik
              </label>
              <select
                value={formData.pole_id || ''}
                onChange={(e) => setFormData({ ...formData, pole_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Žiadny --</option>
                {poles.map((pole) => (
                  <option key={pole.id} value={pole.id}>
                    {pole.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kábel
              </label>
              <select
                value={formData.cable_id || ''}
                onChange={(e) => setFormData({ ...formData, cable_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Žiadny --</option>
                {cables.map((cable) => {
                  const fromPole = poles.find(p => p.id === cable.from_pole_id);
                  const toPole = poles.find(p => p.id === cable.to_pole_id);
                  const cableName = `${fromPole?.name || cable.from_pole_id} → ${toPole?.name || cable.to_pole_id}`;
                  return (
                    <option key={cable.id} value={cable.id}>
                      {cableName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ONU
              </label>
              <input
                type="number"
                value={formData.onu_id || ''}
                onChange={(e) => setFormData({ ...formData, onu_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ONU ID"
              />
            </div>
          </div>

          {/* Assigned to and Deadline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pridelené technikovi
              </label>
              <input
                type="text"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Meno technika"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Termín
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Checklist */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Checklist
            </label>
            <div className="space-y-2">
              {checklist.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={() => handleToggleChecklistItem(item.id)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {item.text}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteChecklistItem(item.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    🗑️
                  </button>
                </div>
              ))}

              {/* Add new checklist item */}
              <div className="flex gap-2 mt-3">
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChecklistItem();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Pridať položku checklistu..."
                />
                <button
                  type="button"
                  onClick={handleAddChecklistItem}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  ➕
                </button>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Poznámky
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Dodatočné poznámky..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Zrušiť
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {task ? 'Uložiť' : 'Vytvoriť'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

